// Phase 9 Plan 04 Task 1 — Pure token substitution over the closed 4-slot grammar.
//
// Grammar (FIXED — validated by Zod at constraint-save time, CSTR-05):
//   {container}            {container.lower}      {container.upper}
//   {subdivision}          {subdivision.lower}    {subdivision.upper}
//   {set}                  {set.lower}            {set.upper}
//   {item}                 {item.lower}           {item.upper}
//
// Tokens NOT matching this grammar are LEFT UNCHANGED — no throw, no panic.
// Rationale: explanation strings may contain literal curly braces in code-like
// content (e.g., "{x: 1}" pseudo-code) or unrelated template markers; the
// pure-function contract is "substitute what I know; leave everything else
// untouched."
//
// SECURITY (T-09-04-01 mitigation): the function returns a plain string. The
// caller renders via `{term}` JSX text-escaping, NEVER via
// `dangerouslySetInnerHTML`. Plan 13 grep gate confirms.
//
// Pure function — no module-mutable state, no side effects, SSR-safe.

import type { VocabularySlot } from "./slots.js";

// Internal token regex — module-private (no leading underscore needed because
// it is not exported). The capture groups are:
//   match[1] — slot name (container / subdivision / set / item)
//   match[2] — full ".lower" / ".upper" suffix (or undefined if base form)
//   match[3] — bare "lower" / "upper" modifier (or undefined)
//
// The slot-name alternation MUST stay byte-identical to VOCABULARY_SLOTS in
// slots.ts. The Zod constraint-save validator and Plan 09-07's authoring UI
// both rely on this grammar; widening it without coordinating those two
// callers would let invalid tokens flow to render time. Add a unit test before
// touching the regex.
const SLOT_TOKEN_RE = /\{(container|subdivision|set|item)(\.(lower|upper))?\}/g;

// Explicit-arity arrow type annotation on the read parameter (no `any` shortcut)
// keeps isolatedDeclarations happy and documents that read is a pure function.
export function substituteExplanation(
  raw: string,
  read: (slot: VocabularySlot) => string,
): string {
  return raw.replace(SLOT_TOKEN_RE, (_match, slot: string, _dotModifier, modifier) => {
    // The regex alternation guarantees `slot` is one of the four literal slot
    // names — cast through unknown is unnecessary because the captured group
    // is constrained by the regex literal. We narrow via the closed type.
    const base: string = read(slot as VocabularySlot);
    if (modifier === "lower") return base.toLowerCase();
    if (modifier === "upper") return base.toUpperCase();
    return base;
  });
}
